import { getCurrentMatchesGradient } from "@/helper/color";
import { getDateWithDay, getTime } from "@/helper/time";
import { getSeasonString } from "@/helper/util";
import { SeasonTeam } from "@/models/Team";
import Link from "next/link";
import {
  MatchWithTeamsAndVenueAndGameday,
  MatchWithTeamsAndVenueAndGamedayAndSeason,
} from "../../models/Match";
import { TeamImage } from "../common/Images";
import { NoItems } from "../common/NoItems";

interface GamescheduleMatchListProps {
  matches: MatchWithTeamsAndVenueAndGamedayAndSeason[];
  baseUrl?: string;
  disableLinks?: boolean;
  openInNewTab?: boolean;
}
export function GamescheduleMatchList({
  matches,
  baseUrl,
  disableLinks = false,
  openInNewTab = false,
}: GamescheduleMatchListProps) {
  if (matches.length === 0)
    return (
      <div className="lg:rounded-md lg:w-full lg:py-10 lg:px-8 lg:bg-white lg:text-center lg:ml-0">
        <NoItems message="Für diesen Spieltag sind noch keine Daten vorhanden" />
      </div>
    );

  return (
    <>
      {matches.map((match) => (
        <GamescheduleMatchCard
          key={match.id}
          match={match}
          baseUrl={baseUrl}
          disableLinks={disableLinks}
          openInNewTab={openInNewTab}
        />
      ))}
    </>
  );
}

interface GamescheduleMatchCardProps {
  match: MatchWithTeamsAndVenueAndGamedayAndSeason;
  baseUrl?: string;
  disableLinks?: boolean;
  openInNewTab?: boolean;
}
function GamescheduleMatchCard({
  match,
  baseUrl,
  disableLinks = false,
  openInNewTab = false,
}: GamescheduleMatchCardProps) {
  const cardContent = (
    <>
      <div
        className={`absolute left-0 top-0 bottom-0 w-2 h-full bg-gradient-to-b ${getCurrentMatchesGradient(
          match.timeStamp,
          match.matchState
        )}`}
      />
      <div className="w-full">
        <div className="flex flex-col">
          <div className="flex justify-between mb-2">
            <div className="text-sm text-gray-dark">{match.gameday.name}</div>
            <div className="text-sm text-gray-dark uppercase">
              {getDateWithDay(match.timeStamp)}
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <div className={`flex flex-col w-full`}>
            <GamescheduleMatchCardTeam
              team={match.homeTeam.seasonTeam}
              match={match}
            />
            <div className=" bg-gray-light h-px  ml-[58px] my-2" />
            <GamescheduleMatchCardTeam
              team={match.awayTeam.seasonTeam}
              match={match}
              isAway
            />
          </div>
          {match.matchState === "Inactive" && (
            <div
              className={`self-center pl-4 font-antonio font-bold text-xl text-center text-transparent bg-clip-text bg-gradient-to-b ${getCurrentMatchesGradient(
                match.timeStamp,
                match.matchState
              )}`}
            >
              {getTime(match.timeStamp)}
              <br />
              Uhr
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (disableLinks) {
    return (
      <div className="flex relative items-center mb-1 bg-white p-4 pl-6">
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      href={`${baseUrl ? baseUrl : ""}/${
        match.league === "Bundesliga" ? "bundesliga" : "pokal"
      }/gameday/${getSeasonString(match.season)}/${match.gameday.index}/${
        match.id
      }`}
      className="flex relative items-center mb-1 bg-white p-4 pl-6"
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}
    >
      {cardContent}
    </Link>
  );
}

const GamescheduleMatchCardTeam = ({
  team,
  match,
  isAway = false,
}: {
  team: SeasonTeam;
  match: MatchWithTeamsAndVenueAndGameday;
  isAway?: boolean;
}) => {
  const isActive = match.matchState !== "Inactive";
  return (
    <div className="flex items-center space-x-2 w-full lg:space-x-6">
      <TeamImage team={team} size="lg" />

      <div className={`flex-1 font-antonio font-bold text-xl uppercase`}>
        {team.name}
      </div>
      {isActive && (
        <div className="font-antonio font-bold text-xl">
          {isAway ? match.awayGames : match.homeGames}
        </div>
      )}
    </div>
  );
};
